/**
 * Include fallback patterns only for major providers to avoid overly broad logo matches.
 * NOTE: Keep keys sorted alphabetically for easier maintenance.
 */
export const liquidityProviderFallbackMatch: Record<string, RegExp> = {
  aborean: /^aborean/,
  aerodrome: /^aerodrome/,
  apeswap: /^apeswap/,
  balancer: /^balancer/,
  curve: /^curve/,
  fluid: /^fluid/,
  hybra: /^hybra(?:_v[4])?/,
  hyperswap: /^hyperswap_v[23]/,
  iziswap: /^iziswap/,
  kyberswap: /^kyberswap/,
  maverick: /^maverick/,
  pancakeswap: /^pancakeswap/,
  quickswap: /^quickswap/,
  ramses: /^ramses/,
  syncswap: /^syncswap/,
  sushiswap: /^sushiswap/,
  traderjoe: /^traderjoe/,
  ultrasolid: /^ultrasolid/,
  uniswap: /^uniswap_(?!v[34](?:_|$))/,
  uniswapv3: /^uniswap_v[34]/,
  velodrome: /^velodrome/,
  zebra: /^zebra/,
};

/**
 * A data structure mapping liquidity provider names to their logo image filename.
 * NOTE: Keep keys sorted alphabetically for easier maintenance.
 */
export const liquidityProviderLogos: Record<string, string> = {
  '0x': '0x',
  '0x_rfq': '0x',
  '1dex': '1dex',
  'aave_v2': 'aave',
  'aave_v3': 'aave',
  'aavev2': 'aave',
  'aavev3': 'aave',
  'aborean': 'aborean',
  'acryptos': 'acryptos',
  'aerodrome': 'aerodrome',
  'agni': 'agni',
  'agni_finance': 'agni',
  'aldrin': 'aldrin',
  'aldrin_v2': 'aldrin',
  'alien_base_v3': 'alienbase',
  'alienbase': 'alienbase',
  'alienbase_stable': 'alienbase',
  'alienbase_v2': 'alienbase',
  'alienbase_v3': 'alienbase',
  'ambient': 'ambient',
  'apeswap': 'apeswap',
  'atlantis_v4': 'atlantis',
  'babydogeswap': 'babydogeswap',
  'bakeryswap': 'bakeryswap',
  'balancer': 'balancer',
  'bancorv3': 'bancor',
  'baseline': 'baseline',
  'baseswap': 'baseswap',
  'basex': 'basex',
  'beanexchange': 'beanexchange',
  'bebop': 'bebop',
  'bebop_rfq': 'bebop',
  'beethoven_x_optimism': 'beethovenx',
  'beethovenx': 'beethovenx',
  'belt': 'belt',
  'biswap': 'biswap',
  'bladeswap': 'bladeswap',
  'bladeswap_v2': 'bladeswap',
  'bladeswap_v3': 'bladeswap',
  'bonkswap': 'bonkswap',
  'butter_swap': 'butterswap',
  'camelot_v2': 'camelot',
  'camelot_v3': 'camelot',
  'camelotv3': 'camelot',
  'cleo_v1': 'cleo',
  'cleo_v2': 'cleo',
  'clober_v2': 'clober',
  'compound': 'compound',
  'crema': 'crema',
  'cropper': 'cropper',
  'crust_v2': 'crust',
  'cryptocom': 'cryptocom',
  'curve': 'curve',
  'dackieswap_v3': 'dackieswap',
  'daos.fun': 'daosfun',
  'defi_swap': 'defiswap',
  'dexlab': 'dexlab',
  'dfyn': 'dfyn',
  'dodo': 'dodo',
  'dodo_v1': 'dodo',
  'dodo_v2': 'dodo',
  'dodov2': 'dodo',
  'dyson': 'dyson',
  'echodex_v3': 'echodex',
  'ekubo': 'ekubo',
  'ellipsis': 'ellipsis',
  'ellipsis_finance': 'ellipsis',
  'equalizer': 'equalizer',
  'fenix_cl': 'fenixcl',
  'fenix_finance': 'fenixcl',
  'fenix_v2': 'fenixcl',
  'firebirdoneswap': 'firebirdoneswap',
  'fluid': 'fluid',
  'fluxbeam': 'fluxbeam',
  'fraxswap_v2': 'fraxswap',
  'fusionx_v2': 'fusionx',
  'fusionx_v3': 'fusionx',
  'gmx': 'gmx',
  'gmx_v1': 'gmx',
  'guacswap': 'guacswap',
  'helium_network': 'heliumnetwork',
  'infusion': 'infusion',
  'integral': 'integral',
  'invariant': 'invariant',
  'ironswap': 'ironswap',
  'iziswap': 'iziswap',
  'jetswap': 'jetswap',
  'kim': 'kim',
  'kim_v2': 'kim',
  'kim_v4': 'kim',
  'kinetix': 'kinetix',
  'kittenswap': 'kittenswap',
  'knightswap': 'knightswap',
  'kona': 'kona',
  'kona_stableswap': 'kona',
  'kona_v2': 'kona',
  'ktx.finance': 'ktx',
  'kuru': 'kuru',
  'kyberdmm': 'kyberswap',
  'kyberelastic': 'kyberswap',
  'kyberswap': 'kyberswap',
  'lido': 'lido',
  'lifinity_v1': 'lifinity',
  'lifinity_v2': 'lifinity',
  'lynex': 'lynex',
  'lynex_fusion': 'lynex',
  'lynex_linea': 'lynex',
  'maker_psm': 'maker',
  'makerpsm': 'maker',
  'maverick': 'maverick',
  'mdex': 'mdex',
  'merchant_moe_liquidity_book_mantle': 'merchantmoe',
  'merchant_moe_mantle': 'merchantmoe',
  'merchantmoe_v1': 'merchantmoe',
  'merchantmoe_v2.2': 'merchantmoe',
  'mercurial': 'mercurial',
  'meshswap': 'meshswap',
  'metavault_v2': 'metavault',
  'metavault_v3': 'metavault',
  'meteora': 'meteora',
  'meteora_dlmm': 'meteora',
  'methlab_swap': 'methlabswap',
  'metric': 'metric',
  'mimswap': 'mimswap',
  'mobiusmoney': 'mobiusmoney',
  'mondaytrade': 'mondaytrade',
  'monoswap': 'monoswap',
  'monoswap_v2': 'monoswap',
  'monoswap_v3': 'monoswap',
  'mooniswap': 'mooniswap',
  'moonshot': 'moonshot',
  'morpheusswap': 'morpheusswap',
  'morphex': 'morphex',
  'mstable': 'mstable',
  'nabla': 'nabla',
  'native_v2': 'native',
  'nerve': 'nerve',
  'nest': 'nest',
  'nile': 'nile',
  'nile_v1': 'nile',
  'nile_v2': 'nile',
  'nuri_cl': 'nuri',
  'nuri_v2': 'nuri',
  'oasis': 'oasis',
  'obric_v2': 'obric',
  'octoswap_v1': 'octoswap',
  'octoswap_v2': 'octoswap',
  'openbook': 'openbook',
  'openbook_v2': 'openbook',
  'orca': 'orca',
  'orca_v1': 'orca',
  'orca_v2': 'orca',
  'origin': 'origin',
  'orion_v2': 'orion',
  'overnight': 'overnight',
  'pancakeswap': 'pancakeswap',
  'pangolin': 'pangolin',
  'penguin': 'penguin',
  'perena': 'perena',
  'perps': 'perps',
  'pharaoh_cl': 'pharaoh',
  'pharaoh_exchange': 'pharaoh',
  'pharaoh_exchange_v1': 'pharaoh',
  'phoenix': 'phoenix',
  'platypus': 'platypus',
  'prjx': 'prjx',
  'project_x': 'prjx',
  'pump.fun': 'pumpfun',
  'purps_v2': 'purps',
  'quickswap': 'quickswap',
  'raydium': 'raydium',
  'raydium_clmm': 'raydium',
  'raydium_cp': 'raydium',
  'retro': 'retro',
  'ringswap': 'ringswap',
  'rocketpool': 'rocketpool',
  'roguex': 'roguex',
  'roguex_v1': 'roguex',
  'roguex_v2': 'roguex',
  'rubicon': 'rubicon',
  'saber': 'saber',
  'saber_(decimals)': 'saber',
  'saddle': 'saddle',
  'sanctum': 'sanctum',
  'sanctum_infinity': 'sanctum',
  'saros': 'saros',
  'secta': 'secta',
  'secta_v2': 'secta',
  'secta_v3': 'secta',
  'shibaswap': 'shibaswap',
  'sky_migration': 'skymigration',
  'skydrome': 'skydrome',
  'solayer': 'solayer',
  'solfi': 'solfi',
  'solidly_v3': 'solidly',
  'soswap': 'soswap',
  'spark': 'spark',
  'spartadex': 'spartadex',
  'spiritswap': 'spiritswap',
  'spookyswap': 'spookyswap',
  'squadswap_v2': 'squadswap',
  'stabble_stable_swap': 'stabble',
  'stabble_weighted_swap': 'stabble',
  'stepn': 'stepn',
  'stratum_exchange': 'stratumexchange',
  'supswap_v2': 'supswap',
  'supswap_v3': 'supswap',
  'sushiswap': 'sushiswap',
  'swapbased': 'swapbased',
  'swapbased_v3': 'swapbased',
  'swapmode': 'swapmode',
  'swapmode_v2': 'swapmode',
  'swapmode_v3': 'swapmode',
  'swapsicle_v2': 'swapsicle',
  'synapse': 'synapse',
  'syncswap': 'syncswap',
  'synthetix': 'synthetix',
  'synthswap_v2': 'synthswap',
  'synthswap_v3': 'synthswap',
  'tesseraswap': 'tesseraswap',
  'thena': 'thena',
  'thena_fusion': 'thena',
  'tokan': 'tokan',
  'token_mill': 'tokenmill',
  'traderjoe': 'traderjoe',
  'ubeswap': 'ubeswap',
  'uniswap': 'uniswap',
  'velodrome': 'velodrome',
  'virtuals': 'virtuals',
  'waultswap': 'waultswap',
  'whirlpool': 'orca',
  'wombat': 'wombat',
  'woofi': 'woofi',
  'woofi_v2': 'woofi',
  'wrapped_mlt': 'wmlt',
  'xfai': 'xfai',
  'yearn': 'yearn',
  'yearn_v3': 'yearn',
  'yoshi': 'yoshi',
  'z_protocol': 'zprotocol',
  'zebra': 'zebra',
};
